import { CreativeProductInstruction, choosePrimaryProduct, deriveProductTypeBucket, isImageUploadProduct } from 'utils/product'
import { FC, Fragment, useCallback, useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { applyFeeDiscount, formatPrice } from 'utils/price'

import { AssignmentDTOCreative } from 'models/assignment'
import { AssignmentStage } from 'constants/assignment'
import { DealState } from 'constants/deal'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { GRAY_700 } from 'constants/styling/theme'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { OpenInNew } from '@mui/icons-material'
import { Path } from 'constants/router'
import { externalNavigate } from 'utils/helpers'
import { logger } from 'utils/logging'
import { useOpenCreativeGuidelines } from 'utils/hooks'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const stagesUnlockingUpload = new Set([
  AssignmentStage.CREATIVE_BOOKED,
])

const stagesUnlockingGalleryLink = new Set([
  AssignmentStage.VISUALS_SENT_BY_CT_TO_BKBN,
  AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR,
  AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN,
  AssignmentStage.VISUALS_SENT_TO_CLIENT,
])

interface Props {
  assignmentData: AssignmentDTOCreative
}

export const AssignmentListItem: FC<Props> = ({ assignmentData }) => {
  const { t } = useTranslation(['deal_assignment_card', 'deal_assignment', 'time_translations', 'category', 'product', 'common'])
  const openGuidelines = useOpenCreativeGuidelines()
  const navigate = useNavigate()

  const product = choosePrimaryProduct(assignmentData.products)

  const isImageUpload = useMemo(() => !!product && isImageUploadProduct(product), [product])

  const handleUpload = useCallback(() => {
    // Guard on the primary product instead of the deprecated assignment `type` (ISSUE-2745):
    // without a product the upload route cannot be decided.
    if (!product) {
      logger.warn('Upload click ignored - assignment has no primary product', undefined, {
        context: { component: 'AssignmentListItem', action: 'handleUpload', metadata: { assignmentId: assignmentData.id } },
      })
      return
    }

    if (!isImageUpload) externalNavigate(Path.GALLERY.replace(':id', assignmentData.id))
    else navigate(Path.CT_UPLOAD.replace(':id', assignmentData.id))

  }, [assignmentData.id, product, isImageUpload, navigate])

  return (
    <BorderBoxWrapper p={1.7} elevation="sm">
      <Stack direction="row" gap={2} width="100%" flexWrap="wrap">

        <Stack flex="1 1 30rem" alignItems="flex-start" gap={.5}>

          <MUIBadge label={`#${assignmentData.id}`} color="gray" size="xs" sx={{ mb: .5 }} />

          {!product &&
            <Typography variant="h4">
              {assignmentData.products.map(prod => {
                const productName = t(`product:p_${prod.id}`)
                return prod.quantity > 1 ? `${prod.quantity} x ${productName}` : productName
              }).join(', ') || t('common:not_applicable')}
            </Typography>
          }

          <Typography variant="text-sm" color={GRAY_700}>

            {!!product &&
              <CreativeProductInstruction
                category={product.category}
                type={deriveProductTypeBucket(product)}
                kind={product.kind}
                attributeX={assignmentData.creativeInstructionAttributeX}
                attributeY={assignmentData.creativeInstructionAttributeY}
                count={product.quantity}
                segment={product.segments[0]}
              />
            }

            {!product &&
              <Fragment>
                {assignmentData.products.map(prod => {
                  const productName = t(`product:p_${prod.id}`)
                  return prod.quantity > 1 ? `${prod.quantity} x ${productName}` : productName
                }).join(', ') || t('common:not_applicable')}
              </Fragment>
            }

          </Typography>

        </Stack>

        <Stack flex="1 0 auto" alignItems={{ xs: 'flex-start', sm: 'flex-end' }} justifyContent="space-between" gap={1}>

          <Typography variant="h4">
            {formatPrice(applyFeeDiscount(assignmentData.creativeRemuneration.baseRemuneration))}
          </Typography>

          {stagesUnlockingUpload.has(assignmentData.stage) && assignmentData.orderState !== DealState.CANCELLED &&
            <MUIButton
              onClick={() => handleUpload()}
            >
              {t('upload_visuals')}
            </MUIButton>
          }

          {!stagesUnlockingGalleryLink.has(assignmentData.stage) &&
            <MUIButton
              type="secondaryBorder"
              startIcon={<OpenInNew fontSize="large" />}
              onClick={() => openGuidelines.open({ productKind: product?.kind, creativeInstructionsPairingId: assignmentData.creativeInstructionsPairingId, legacyExternalUrl: assignmentData.creativeInstructionsUrl })}
            >
              {t('deal_assignment:follow_instruction_button')}
            </MUIButton>
          }

          {stagesUnlockingGalleryLink.has(assignmentData.stage) &&
            <MUIButton
              type="secondaryBorder"
              startIcon={<OpenInNew fontSize="large" />}
              onClick={() => externalNavigate(Path.GALLERY.replace(':id', assignmentData.id))}
            >
              {t('see_visuals')}
            </MUIButton>
          }

        </Stack>

      </Stack>
    </BorderBoxWrapper>
  )
}
